import React, { useState, useEffect } from 'react';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import Input from '@material-ui/core/Input';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemIcon from '@material-ui/core/ListItemIcon';

const formatDate = (date) => {
  const d = new Date(date);
  return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth()+1).toString().padStart(2, '0')}/${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '2rem',
    color: theme.palette.text.primary,
  },
  inputContainer: {
    display: 'flex',
    width: '100%',
    marginBottom: '1rem'
  },
  input: {
    flexGrow: 1,
    marginRight: '1rem'
  },
  listContainer: {
    width: '100%',
    height: '100%',
    marginTop: '1rem',
    backgroundColor: theme.mode === 'dark' ? '#333333' : '#f3f3f3',
    borderRadius: '10px',
  },
  cardItem: {
    marginBottom: '10px',
    padding: '10px',
    borderRadius: '8px',
    boxShadow: theme.mode === 'dark' 
      ? '0 2px 4px rgba(255, 255, 255, 0.1)' 
      : '0 2px 4px rgba(0, 0, 0, 0.1)',
    backgroundColor: theme.mode === 'dark' ? '#2a2a2a' : '#f3f3f3',
    color: theme.palette.text.primary,    
    alignItems: 'center',    
  },  
  inputInline: {
    width: '100%',
    fontSize: '1rem',
    padding: '4px 0',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  textContainer: {
    flex: 1,
  },
  iconButton: {
    color: theme.palette.text.primary,
  },
  taskText: {
    fontSize: '1rem',
    wordBreak: 'break-word',
    transition: 'all 0.3s ease',
  },
  completedTask: {
    textDecoration: 'line-through',
    color: theme.palette.text.disabled,
  },
  checkboxRoot: {
    color: theme.palette.success.main,
    '&$checked': {
      color: theme.palette.success.main,
    },
  },
  checked: {}, // usado junto com checkboxRoot
}));

const ToDoList = () => {    
  const [editingId, setEditingId] = useState(null);
  const handleInlineEdit = (index) => {
    setEditingId(index);
  };

  const handleInlineSave = (index, newText) => {
    const updatedTasks = [...tasks];
    updatedTasks[index].text = newText;
    updatedTasks[index].updatedAt = new Date();
    setTasks(updatedTasks);
    setEditingId(null);
  };

  const toggleComplete = (index) => {
  const updatedTasks = [...tasks];
    updatedTasks[index].completed = !updatedTasks[index].completed;
    updatedTasks[index].updatedAt = new Date(); // opcional: atualiza data ao concluir
    setTasks(updatedTasks);
  };
  const classes = useStyles();

  const [task, setTask] = useState('');
  const [tasks, setTasks] = useState([]);
  const [editIndex, setEditIndex] = useState(-1);

  useEffect(() => {
    const savedTasks = localStorage.getItem('tasks');
    if (savedTasks) {
      setTasks(JSON.parse(savedTasks));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('tasks', JSON.stringify(tasks));
  }, [tasks]);

  const handleTaskChange = (event) => {
    setTask(event.target.value);
  };

  const handleAddTask = () => {
    if (!task.trim()) return; // Impede que o usuário crie uma tarefa sem texto
    
    const now = new Date();
    if (editIndex >= 0) {
      // Editar tarefa existente
      const newTasks = [...tasks];
      newTasks[editIndex] = {text: task, updatedAt: now, createdAt: newTasks[editIndex].createdAt};
      setTasks(newTasks);
      setTask('');
      setEditIndex(-1);
    } else {
      // Adicionar nova tarefa
      setTasks([
        ...tasks, 
        {
          text: task, 
          createdAt: now, 
          updatedAt: now,
          completed: false,
        }
      ]);
      setTask('');
    }
  };

  // const handleEditTask = (index) => {
  //   setTask(tasks[index].text);
  //   setEditIndex(index);
  // };

  const handleDeleteTask = (index) => {
    const newTasks = [...tasks];
    newTasks.splice(index, 1);
    setTasks(newTasks);
  };

  const pendingTasksCount = tasks.filter(t => !t.completed).length;

  const [showOnlyPending, setShowOnlyPending] = useState(false);

  return (
    <div className={classes.root}>
      <div className={classes.inputContainer}>
        <TextField
          className={classes.input}
          label="Nova tarefa"
          value={task}
          onChange={handleTaskChange}
          variant="outlined"
        />
        <Button variant="contained" color="primary" onClick={handleAddTask}>
          {editIndex >= 0 ? 'Salvar' : 'Adicionar'}
        </Button>
      </div>
      <div style={{ width: '100%', textAlign: 'left', marginBottom: '1rem' }}>
        <strong>Você tem {pendingTasksCount} tarefa{pendingTasksCount !== 1 ? 's' : ''} pendente{pendingTasksCount !== 1 ? 's' : ''}</strong>
      </div>
      <div style={{ width: '100%', textAlign: 'left', marginBottom: '1rem' }}>
        <Button
          variant="outlined"
          size="small"
          color={showOnlyPending ? "primary" : "default"}
          onClick={() => setShowOnlyPending(!showOnlyPending)}
        >
          {showOnlyPending ? 'Mostrar todas' : 'Apenas pendentes'}
        </Button>
      </div>
      <div className={classes.listContainer}>
        {tasks.filter(task => !showOnlyPending || !task.completed)
              .map((task, index) => (
          <Paper key={index} className={classes.cardItem}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ marginRight: 8, display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  edge="start"
                  checked={task.completed || false}
                  tabIndex={-1}
                  disableRipple
                  onChange={() => toggleComplete(index)}
                  classes={{
                    root: classes.checkboxRoot,
                    checked: classes.checked,
                  }}
                />
              </div>

              {editingId === index ? (
                <Input
                  className={classes.inputInline}
                  value={task.text}
                  onChange={(e) => {
                    const updatedTasks = [...tasks];
                    updatedTasks[index].text = e.target.value;
                    setTasks(updatedTasks);
                  }}
                  onBlur={() => handleInlineSave(index, task.text)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleInlineSave(index, task.text);
                    }
                  }}
                  autoFocus
                  fullWidth
                />
              ) : (
                <div className={`${classes.textContainer} ${task.completed ? classes.completedTask : ''}`}>
                  <div className={classes.taskText}>{task.text}</div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                    Última edição: {formatDate(task.updatedAt)}
                  </div>
                </div>
              )}

              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                {!task.completed && editingId !== index && (
                  <IconButton
                    size="small"
                    className={classes.iconButton}
                    onClick={() => handleInlineEdit(index)}
                    aria-label="editar"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  className={classes.iconButton}
                  onClick={() => handleDeleteTask(index)}
                  aria-label="excluir"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>
            </div>

            {/* Botão Salvar quando está editando */}
            {editingId === index && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => handleInlineSave(index, task.text)}
                >
                  Salvar
                </Button>
              </div>
            )}
          </Paper>
        ))}
      </div>
    </div>
  );
};


export default ToDoList;
